<template>
  <Card
    class="bids-content"
    :class="{
      'bids-content--expanded': isExpanded,
      'z-75': isExpanded,
    }"
  >
    <div class="bids-content__header">
      <div class="row">
        <div class="columns">
          <KeywordFilters
            :search-placeholder="$t('ADVERTISEMENT.BIDS.SEARCH_BID')"
            :search.sync="filters.search"
            :range="range"
            @update:range="updateRange"
            @update:search="resetSelection"
          />
        </div>

        <div class="columns shrink">
          <BidsContentExportCSV :range="range" />
        </div>
      </div>

      <div class="row bids-content__actions-row">
        <div
          class="columns large-6 medium-6 small-12 bids-content__actions-column"
        >
          <AddBid />

          <BidsActions
            :selected-ids="selectedIds"
            class="bids-content__actions"
            @delete="resetSelection"
          />
        </div>

        <div
          class="columns large-6 medium-6 small-12 bids-content__actions-column bids-content__actions-column--reversed"
        >
          <IconicButton
            :text="$t('ADVERTISEMENT.REFRESH')"
            icon="refresh"
            @click="getBids"
          />

          <FullScreenButton v-model="isExpanded" />

          <KeywordColumnSettings
            :visible-columns.sync="visibleColumns"
            :hidden-columns.sync="hiddenColumns"
            :locked-columns="lockedColumns"
            @update:visible-columns="setVisibleColumns"
          />
        </div>
      </div>
    </div>
    <KeywordList
      :columns="allColumns"
      :bids="filteredData"
      :reset-selection-controller="resetSelectionController"
      @selection-change="selectionChange"
    />
  </Card>
</template>
<script>
import { mapGetters } from 'vuex';

import { CAMPAIGN_BIDS, CAMPAIGN_BIDS_META_DATA } from '@/store/types';
import campaignId from '@/mixins/advertisement/campaignId';
import modal from '@/mixins/modal';

import KeywordFilters from '@@/advertisement/common/keyword/KeywordFilters';
import Card from '@@/common/Card';

import bidsColumnsStorageHandler from './utils/bidsColumnsStorage';

import KeywordColumnSettings from '@@/advertisement/common/keyword/KeywordColumnSettings';
import AddBid from './AddBid';

import BidsActions from './BidsActions';
import BidsContentExportCSV from './BidsContentExportCSV';

import IconicButton from '@@/advertisement/common/IconicButton';
import KeywordList from '@@/advertisement/common/KeywordList';
import FullScreenButton from '@@/advertisement/common/FullScreenButton';

import { columns } from './constants';

import createController from '@/utils/controller';

const resetSelectionController = createController('resetSelection');

let bidsColumnsStorage = null;

export default {
  name: 'BidsContent',

  components: {
    FullScreenButton,
    AddBid,
    KeywordColumnSettings,
    KeywordFilters,
    Card,
    BidsActions,
    IconicButton,
    KeywordList,
    BidsContentExportCSV,
  },
  mixins: [modal, campaignId],
  props: {
    range: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isExpanded: false,

      selectedIds: [],

      filters: {
        search: '',
      },

      availableColumns: {
        hideFromSetting: [],
        static: [],
        dynamic: [],
      },
      visibleColumns: [],
      hiddenColumns: [],
    };
  },
  computed: {
    ...mapGetters({
      data: CAMPAIGN_BIDS,
      metadata: CAMPAIGN_BIDS_META_DATA,
    }),
    allColumns() {
      return [
        ...this.availableColumns.hideFromSetting,
        ...this.availableColumns.static,
        ...this.visibleColumns,
      ];
    },
    lockedColumns() {
      return this.availableColumns.static;
    },
    filteredData() {
      const { search } = this.filters;
      if (!search) return this.data;
      return this.data.filter(data =>
        data.query.toLowerCase().includes(search.toLowerCase()),
      );
    },
    resetSelectionController() {
      return resetSelectionController;
    },
  },
  created() {
    this.initialteColumnStorageHandler();
  },
  methods: {
    initialteColumnStorageHandler() {
      this.availableColumns = columns(this.metadata);
      bidsColumnsStorage = bidsColumnsStorageHandler(
        this.availableColumns.dynamic,
      );
      this.visibleColumns = bidsColumnsStorage.getVisibleColumns();
      this.hiddenColumns = bidsColumnsStorage.getHiddenColumns();
    },
    updateRange(range) {
      this.$emit('update:range', range);
    },
    getBids() {
      this.$emit('update');
    },
    selectionChange(selection) {
      this.selectedIds = selection.map(({ id }) => id);
    },
    setVisibleColumns(visibleColumns) {
      bidsColumnsStorage.setVisibleColumns(visibleColumns);
    },
    resetSelection() {
      resetSelectionController.resetSelection();
    },
  },
};
</script>

<style lang="scss">
.bids-content {
  // To increase specificity
  &.bids-content--expanded {
    margin: 0;
    height: 100%;
    overflow-y: scroll;

    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    margin: 0;
    height: 100vh;
    width: 100vw;
  }

  &__header {
    margin-bottom: $global-margin * 2;
  }

  &__actions-row {
    margin-top: $global-margin;
  }

  &__actions-column {
    display: flex;
    align-items: center;

    &--reversed {
      flex-direction: row-reverse;

      @include rtl() {
        .el-button + .el-button {
          margin-left: calc($global-margin / 2);
          margin-right: 0;
        }
      }

      .el-button + .el-button {
        margin-right: calc($global-margin / 2);
        margin-left: 0;
      }
    }
  }

  &__actions {
    margin-left: $global-margin;

    @include rtl() {
      margin-left: 0;
      margin-right: $global-margin;
    }
  }
}
</style>
